/**
 * MOB-3 · Remapeo de teclas. Puro y sin DOM.
 *
 * Hasta ahora las teclas estaban escritas a mano dentro de un `switch` en
 * `input.ts`, lo que dejaba fuera a cualquiera que no use un teclado QWERTY o
 * que no pueda alcanzar cómodamente WASD. El mapa se saca aquí para que sea
 * dato y no código.
 *
 * La clave (nunca mejor dicho) es qué se guarda. Se usa `KeyboardEvent.key`
 * normalizado a minúsculas y NO `event.code`: `code` es la posición FÍSICA de la
 * tecla, así que en un teclado AZERTY las teclas "WASD" están donde un francés
 * lee ZQSD y guardar la posición le ata a la disposición de otro. Guardando el
 * carácter que la tecla produce, cada quien reasigna lo que ve impreso.
 */

/** Acciones remapeables. */
export type Action = "up" | "down" | "left" | "right" | "kick";

export const ACTIONS: readonly Action[] = ["up", "down", "left", "right", "kick"];

/** Etiquetas para la UI de ajustes. */
export const ACTION_LABELS: Record<Action, string> = {
  up: "Arriba",
  down: "Abajo",
  left: "Izquierda",
  right: "Derecha",
  kick: "Patear / cargar",
};

/** Tecla → acción. Varias teclas pueden compartir acción (flechas + WASD). */
export type KeyBindings = Record<Action, string[]>;

/**
 * Normaliza una tecla a la forma en que se guarda.
 *
 * La barra espaciadora es el caso especial: los navegadores antiguos mandan
 * `"Spacebar"` y los actuales `" "`, y un espacio como clave de configuración
 * es invisible al depurar. Se guarda como `"space"`.
 */
export function normalizeKey(key: string): string {
  if (key === " " || key === "Spacebar" || key === "Space") return "space";
  return key.length === 1 ? key.toLowerCase() : key;
}

/** Etiqueta legible de una tecla guardada (para pintarla en los ajustes). */
export function keyLabel(key: string): string {
  if (key === "space") return "ESPACIO";
  const arrows: Record<string, string> = {
    ArrowUp: "↑", ArrowDown: "↓", ArrowLeft: "←", ArrowRight: "→",
  };
  return arrows[key] ?? key.toUpperCase();
}

/** Mapa por defecto: flechas y WASD a la vez, espacio para patear. */
export function defaultBindings(): KeyBindings {
  return {
    up: ["ArrowUp", "w"],
    down: ["ArrowDown", "s"],
    left: ["ArrowLeft", "a"],
    right: ["ArrowRight", "d"],
    kick: ["space"],
  };
}

/**
 * Índice inverso tecla→acción, que es como lo consulta el manejador de eventos.
 *
 * Se construye una vez al cambiar el mapa y no en cada pulsación: esto corre en
 * el camino del input, y recorrer cinco listas por cada `keydown` de un jugador
 * que aporrea el teclado es trabajo regalado.
 */
export function buildKeyIndex(binds: KeyBindings): Map<string, Action> {
  const index = new Map<string, Action>();
  for (const action of ACTIONS) {
    for (const key of binds[action] ?? []) index.set(normalizeKey(key), action);
  }
  return index;
}

/**
 * Asigna `key` a `action`, quitándosela a quien la tuviera.
 *
 * Una tecla no puede hacer dos cosas: si se permitiera, la primera reasignación
 * descuidada dejaría al jugador moviéndose y pateando con la misma tecla sin
 * entender por qué. Devuelve un mapa NUEVO (no muta el recibido).
 */
export function assignKey(binds: KeyBindings, action: Action, key: string): KeyBindings {
  const k = normalizeKey(key);
  const next = {} as KeyBindings;
  for (const a of ACTIONS) next[a] = (binds[a] ?? []).filter((x) => normalizeKey(x) !== k);
  next[action] = [...next[action], k];
  return next;
}

/**
 * Quita una tecla de una acción, pero nunca la última: una acción sin ninguna
 * tecla es un jugador que no puede moverse y que solo puede arreglarlo
 * restaurando los valores por defecto.
 */
export function unassignKey(binds: KeyBindings, action: Action, key: string): KeyBindings {
  const current = binds[action] ?? [];
  if (current.length <= 1) return binds;
  const k = normalizeKey(key);
  const next = { ...binds };
  next[action] = current.filter((x) => normalizeKey(x) !== k);
  return next;
}

/**
 * Sanea un mapa venido de `localStorage` (que cualquiera puede editar a mano).
 * Toda acción acaba con al menos una tecla, cayendo a la de por defecto.
 */
export function sanitizeBindings(raw: unknown): KeyBindings {
  const def = defaultBindings();
  if (!raw || typeof raw !== "object") return def;
  const src = raw as Record<string, unknown>;
  const out = {} as KeyBindings;
  for (const action of ACTIONS) {
    const list = src[action];
    const keys = Array.isArray(list)
      ? list.filter((k): k is string => typeof k === "string" && k.length > 0).map(normalizeKey)
      : [];
    // Sin duplicados y nunca vacía.
    const unique = [...new Set(keys)];
    out[action] = unique.length > 0 ? unique : def[action];
  }
  return out;
}

/** Teclas cuyo comportamiento por defecto del navegador hay que cancelar
 *  (las flechas hacen scroll y el espacio hace avanzar página). */
export function shouldPreventDefault(key: string): boolean {
  const k = normalizeKey(key);
  return k === "space" || k === "ArrowUp" || k === "ArrowDown" || k === "ArrowLeft" || k === "ArrowRight";
}
